import React from 'react';
import type { AttentionTone } from '../../hooks/useAttention';

const BANG: Record<AttentionTone, string> = {
  alert: '!',
  attention: '!',
  soft: '!',
  calm: '✓',
};

export interface AttentionRowProps {
  tone: AttentionTone;
  eyebrow: string;
  title: string;
  meta?: string;
  ctaLabel?: string;
  onCta?: () => void;
  onClick?: () => void;
  className?: string;
  onPointerDown?: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp?: React.PointerEventHandler<HTMLDivElement>;
  onPointerLeave?: React.PointerEventHandler<HTMLDivElement>;
  onPointerCancel?: React.PointerEventHandler<HTMLDivElement>;
}

export function AttentionRow({
  tone,
  eyebrow,
  title,
  meta,
  ctaLabel,
  onCta,
  onClick,
  className,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
}: AttentionRowProps) {
  const accent = `var(--theme-attention-${tone})`;
  const hasCta = !!ctaLabel;

  function handleCtaClick() {
    onCta?.();
  }

  const iconAndContent = (
    <>
      <div
        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold"
        style={{ color: accent, border: `1.5px solid ${accent}`, background: `color-mix(in srgb, ${accent} 10%, transparent)` }}
        aria-hidden="true"
      >
        {BANG[tone]}
      </div>

      <div className="flex-1 min-w-0">
        <div
          className="text-[11px] font-semibold uppercase tracking-wider mb-0.5"
          style={{ color: accent }}
        >
          {eyebrow}
        </div>
        <div className="text-[14px] font-semibold text-text-primary leading-tight truncate">
          {title}
        </div>
        {meta && (
          <div className="text-[12px] text-text-secondary mt-0.5">{meta}</div>
        )}
      </div>
    </>
  );

  const ctaButton = hasCta && (
    <button
      type="button"
      className="flex-shrink-0 text-[12px] font-semibold px-3 py-1.5 rounded-md transition-opacity hover:opacity-80 active:opacity-60"
      style={
        tone === 'calm'
          ? { background: 'transparent', color: accent, border: `1px solid color-mix(in srgb, ${accent} 25%, transparent)` }
          : // text-on-accent (not text-inverse): the background is a fixed hex, so the
            // text token must not flip per theme — white on #B04A4A is 5.35:1 (AA).
            { background: accent, color: 'var(--theme-text-on-accent)', border: 'none' }
      }
      onClick={handleCtaClick}
      aria-label={ctaLabel}
    >
      {ctaLabel} →
    </button>
  );

  const rowClassName = `flex items-center gap-3 px-4 py-3 rounded-lg select-none ${
    onClick ? 'cursor-pointer transition-opacity hover:opacity-90 active:opacity-75' : ''
  } ${className ?? ''}`;
  const rowStyle: React.CSSProperties = {
    background: 'var(--theme-attention-bg)',
    borderLeft: `3px solid ${accent}`,
  };

  // A CTA <button> nested inside a role="button" row is an axe `nested-interactive`
  // violation — real, and hit in production (AttentionStrip.tsx passes both onClick
  // and ctaLabel together): AT can't tell which control actually activates. When
  // both are present, only the icon+text region becomes the focusable/clickable
  // control (a <button>, a SIBLING of the CTA button, never its ancestor); the row
  // itself stays a plain, non-interactive div. (Spec 279 Phase 7 a11y fix.)
  //
  // The outer div below deliberately OMITS the cursor-pointer/hover classes that
  // `rowClassName` would otherwise carry — this branch's outer div has no onClick
  // of its own (only the inner button and the CTA button do), so a pointer cursor
  // or hover-dim over its padding/gutter would be a false affordance (padding
  // pixels that look clickable but aren't). That hover/cursor treatment moves to
  // the inner content button instead, which IS the real clickable region.
  // FOLLOW-UP (flagged in Spec 279 Phase 7 review, not fixed here): the inner
  // button's own hit-box is still only its intrinsic content size, not the full
  // padded row height/width the old single-div layout gave the equivalent region —
  // expanding it to fill the row (e.g. self-stretch + moving px-4/py-3 onto the
  // button) needs a visual check this session couldn't perform.
  if (onClick && hasCta) {
    return (
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg select-none ${className ?? ''}`}
        style={rowStyle}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
      >
        <button
          type="button"
          className="flex items-center gap-3 flex-1 min-w-0 text-left bg-transparent border-0 p-0 m-0 cursor-pointer transition-opacity hover:opacity-90 active:opacity-75"
          onClick={onClick}
          aria-label={`${eyebrow}: ${title}`}
        >
          {iconAndContent}
        </button>
        {ctaButton}
      </div>
    );
  }

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={rowClassName}
      style={rowStyle}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onPointerCancel={onPointerCancel}
      aria-label={onClick ? `${eyebrow}: ${title}` : undefined}
    >
      {iconAndContent}
      {ctaButton}
    </div>
  );
}
